import React from 'react';

import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {ElementMouseEvent} from 'util/Types';

interface CommonUnarchiveButtonProps {
  buttonStyle?: ButtonStyle;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
  showText?: boolean;
  tooltipTitle?: string;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

interface UnarchiveButtonOnClickProps extends CommonUnarchiveButtonProps {
  onClick: (e: ElementMouseEvent) => void;
}

interface UnarchiveOptions {
  hasRegisteredItems: boolean;
}

interface UnarchiveButtonOnUnarchiveProps extends CommonUnarchiveButtonProps {
  itemIds: Array<string>;
  /**
   * The meaning of this callback changes based on whether you are operating on Core Object types or not:
   * - For Core Objects, `onUnarchive` behaves like a post-action UX hook, because unarchival is handled by
   *   the framework.
   * - For Legacy Objects, `onUnarchive` must perform the unarchival action itself, and may also perform UX
   *   work following the resolution of the network action.
   */
  onUnarchive: (itemIds: Array<string>, unarchiveOptions?: UnarchiveOptions) => void;
  unarchiveOptions?: UnarchiveOptions;
}

type UnarchiveButtonProps = UnarchiveButtonOnClickProps | UnarchiveButtonOnUnarchiveProps;

export default function UnarchiveButton({
  buttonStyle = ButtonStyle.TERTIARY_BLUE,
  className,
  disabled,
  fullWidth,
  showText,
  tooltipTitle = 'Unarchive',
  ...props
}: UnarchiveButtonProps): JSX.Element {
  const handleClick = (e: ElementMouseEvent): void => {
    if (props.onClickTracking) {
      props.onClickTracking();
    }
    Context.usingAsync({analyticsMetadata: props.getAnalyticsMetadata?.()}, null, () => {
      // eslint-disable-next-line benchlint/no-in-operator
      if ('onClick' in props) {
        props.onClick(e);
      } else if (props.unarchiveOptions != null) {
        props.onUnarchive(props.itemIds, props.unarchiveOptions);
      } else {
        props.onUnarchive(props.itemIds);
      }
    });
  };

  return (
    <Tooltip
      text={tooltipTitle}
      placement={FloatingPlacement.TOP}
      toggleDisplayMode={ToggleDisplayMode.BLOCK}
    >
      <Button
        buttonStyle={buttonStyle}
        className={className}
        disabled={disabled}
        fullWidth={fullWidth}
        icon="unarchive"
        onClick={handleClick}
        size={ButtonSize.SMALL}
        text={showText ? 'Unarchive' : null}
        dataTestAttrs={{component: 'UnarchiveButton', element: 'root'}}
      />
    </Tooltip>
  );
}
